import React from 'react'
import '../styles/msrpChart.scss'

const AXIS_STEP = 5
const AXIS_PADDING = 2
const MIN_AXIS_SPAN = 10

const toPercentage = (value, msrp) => {
  if (!msrp || !Number.isFinite(value) || !Number.isFinite(msrp)) {
    return null
  }
  return (value / msrp) * 100
}

const getComparablePercentage = (comp) => {
  if (!comp) {
    return null
  }
  if (Number.isFinite(comp.msrp_percentage)) {
    return comp.msrp_percentage
  }
  return toPercentage(comp.retail_price, comp.msrp)
}

const getAxisBounds = (values) => {
  const max = Math.max(...values)
  const min = Math.min(...values)
  let hi = Math.ceil((max + AXIS_PADDING) / AXIS_STEP) * AXIS_STEP
  let lo = Math.floor((min - AXIS_PADDING) / AXIS_STEP) * AXIS_STEP
  if (hi - lo < MIN_AXIS_SPAN) {
    const center = (hi + lo) / 2
    hi = Math.ceil((center + MIN_AXIS_SPAN / 2) / AXIS_STEP) * AXIS_STEP
    lo = Math.floor((center - MIN_AXIS_SPAN / 2) / AXIS_STEP) * AXIS_STEP
  }
  return { hi, lo }
}

function MSRPChart({ msrp, price, dealerRetailValue, comparables }) {
  if (!msrp || msrp <= 0) {
    return null
  }
  if (!comparables || !comparables.length) {
    return null
  }

  const comparablePercentages = comparables
    .map(getComparablePercentage)
    .filter((value) => Number.isFinite(value))
  if (!comparablePercentages.length) {
    return null
  }

  const vehiclePrice = Number.isFinite(price) ? price : dealerRetailValue
  const vehiclePercentage = toPercentage(vehiclePrice, msrp)
  if (!Number.isFinite(vehiclePercentage)) {
    return null
  }

  const vehicleRounded = Math.round(vehiclePercentage)
  const rangeMin = Math.round(Math.min(...comparablePercentages))
  const rangeMax = Math.round(Math.max(...comparablePercentages))

  const { hi, lo } = getAxisBounds([vehicleRounded, rangeMin, rangeMax])
  const span = hi - lo
  const toX = (value) => ((value - lo) / span) * 100

  const rangeLeft = toX(rangeMin)
  const rangeWidth = toX(rangeMax) - rangeLeft
  const rangeCenter = rangeLeft + rangeWidth / 2
  const markerLeft = toX(vehicleRounded)

  const rangeLabel = rangeMin === rangeMax
    ? `${rangeMin}% of MSRP`
    : `${rangeMin}\u2013${rangeMax}% of MSRP`

  // Tick labels every AXIS_STEP from the low (left) end to the high (right) end.
  const ticks = []
  for (let value = lo; value <= hi; value += AXIS_STEP) {
    ticks.push(value)
  }

  return (
    <div className="msrp-chart">
      <div className="msrp-chart-plot">
        <div className="msrp-chart-track">
          <div
            className="msrp-chart-range"
            style={{ left: `${rangeLeft}%`, width: `${Math.max(rangeWidth, 1)}%` }}
          />
          <div className="msrp-chart-marker" style={{ left: `${markerLeft}%` }}>
            <div className="msrp-chart-tooltip">
              <span className="msrp-chart-tooltip-prefix">This vehicle: </span>
              <strong>{`${vehicleRounded}% of MSRP`}</strong>
            </div>
          </div>
        </div>
        <div className="msrp-chart-range-label" style={{ left: `${rangeCenter}%` }}>
          <span>Similar vehicles</span>
          <span>{rangeLabel}</span>
        </div>
      </div>
      <div className="msrp-chart-axis-labels">
        {ticks.map((value, index) => {
          const isFirst = index === 0
          const isLast = index === ticks.length - 1
          return (
            <span
              key={value}
              className={`msrp-chart-tick${isFirst || isLast ? ' msrp-chart-tick-end' : ''}`}
            >
              <span className="msrp-chart-tick-value">{`${value}%`}</span>
              {isFirst && <span className="msrp-chart-tick-caption">Better value</span>}
              {isLast && <span className="msrp-chart-tick-caption">Higher price</span>}
            </span>
          )
        })}
      </div>
    </div>
  )
}

export default MSRPChart
